import React, { useCallback, useEffect, useRef, useState } from "react";
import DsModal from "../_UI/dsModal";
import Form from "../_UI/dsModal/form.module.scss";
import { useDispatch, useSelector } from "react-redux";
import {
    getDepositLink,
    getDepositLinkFailure,
    getDepositLinkSuccess,
    setMatrixBuyModalStatus,
} from "../../store/cabinet/action";
import ftfIcon from "../../theme/assets/redesign/wallet/token_ftf.webp";
import { useTranslation } from "react-i18next";

const CabinetMatrixBuyModal = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const matrixBuyModalStatus = useSelector((store) => store.cabinet.matrixBuyModalStatus);
    const depositLinkStatus = useSelector((store) => store.cabinet.depositLinkStatus);
    const depositLinkStatusErr = useSelector((store) => store.cabinet.depositLinkStatusErr);
    const userData = useSelector((store) => store.cabinet.userData);

    const [amount, setAmount] = useState("");

    // Ссылка на предварительно открытую вкладку (НЕ закрываем её в closeModal!)
    const payWinRef = useRef(null);

    const rate = Number(matrixBuyModalStatus?.lap?.token_rate) || 0;
    const amountNum = Number(String(amount).replace(",", ".")) || 0;
    const total = Math.round((rate * amountNum + Number.EPSILON) * 100) / 100;

    const fmt2 = (v) =>
        new Intl.NumberFormat("ru-RU", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(v);

    const handleChange = (e) => {
        if (depositLinkStatusErr?.amount) {
            dispatch(getDepositLinkFailure(false));
        }
        let newValue = e.target.value.replace(/[^0-9]/g, "");
        const num = parseFloat(newValue);
        if (!isNaN(num)) {
            newValue = (Math.round(num * 100) / 100).toString();
        }
        setAmount(newValue);
    };

    const closeModal = useCallback(() => {
        // ВАЖНО: пред-вкладку здесь НЕ трогаем
        dispatch(setMatrixBuyModalStatus({ status: false, lap: false }));
        dispatch(getDepositLinkSuccess(false));
        setAmount("");
    }, [dispatch]);

    // Пришёл URL — направляем заранее открытую вкладку и ТОЛЬКО затем закрываем модалку
    useEffect(() => {
        const url = depositLinkStatus?.url;
        if (!url) return;

        try {
            if (payWinRef.current && !payWinRef.current.closed) {
                // Самый надёжный способ для Safari/iOS
                payWinRef.current.location.assign(url);
            } else {
                // Фолбэк, если не удалось заранее открыть
                window.open(url, "_blank", "noopener");
            }
        } catch {
            window.open(url, "_blank", "noopener");
        }

        // Отвязываем окно, чтобы closeModal его не закрыл
        payWinRef.current = null;
        closeModal();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [depositLinkStatus, closeModal]);

    // Если пришла ошибка — закрываем заранее открытую пустую вкладку
    useEffect(() => {
        if (depositLinkStatusErr && payWinRef.current && !payWinRef.current.closed) {
            try {
                payWinRef.current.close();
            } catch {}
            payWinRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [depositLinkStatusErr]);

    const handleConfirm = () => {
        if (amountNum > 0 && !depositLinkStatusErr?.amount) {
            // Открываем пред-вкладку БЕЗ "noopener", чтобы потом можно было навигировать
            const w = window.open("", "_blank");
            if (w) {
                // Немного контента, чтобы не мигал about:blank и вкладка «закрепилась»
                try {
                    w.document.write(`
            <title>${t("Переход к оплате...")}</title>
            <body style="font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial; padding:20px;">
              <p>${t("Ожидайте, перенаправляем на платёжную страницу")}...</p>
            </body>
          `);
                    w.document.close();
                } catch {}
            }
            payWinRef.current = w;

            // Запрашиваем ссылку оплаты
            dispatch(getDepositLink({ amount }));
        }
    };

    const canConfirm = amountNum > 0 && !depositLinkStatusErr?.amount;
    const maxPerLap = userData?.user?.available_token_purchases > 0
        ? userData?.user?.available_token_purchases
        : matrixBuyModalStatus?.lap?.max_purchases_per_user || 0;

    return (
        <DsModal
            open={matrixBuyModalStatus?.status}
            onClose={closeModal}
            title={t("Купить токены")}
            subtitle={t("Введите количество токенов, которые вы хотите купить")}
        >
            <div className={Form.field}>
                <div className={`${Form.inputBox} ${depositLinkStatusErr?.amount ? Form.inputBoxErr : ''}`}>
                    <input
                        type="text"
                        value={amount}
                        onChange={handleChange}
                        inputMode="numeric"
                        placeholder="0"
                        autoFocus
                    />
                    <span className={Form.suffix}>
                        <img src={ftfIcon} alt=""/>
                        FTF
                    </span>
                </div>
                <div className={Form.err}>{depositLinkStatusErr?.amount ? depositLinkStatusErr.amount : null}</div>
            </div>

            <div className={Form.info}>
                <div className={Form.infoRow}>
                    <span>{t("Доступно токенов")}</span>
                    <span>{Number(userData?.user?.tokens) || 0}</span>
                </div>
                <div className={Form.infoRow}>
                    <span>{t("Максимум токенов на круге")}</span>
                    <span>{maxPerLap}</span>
                </div>
                <div className={Form.infoRow}>
                    <span>{t("Курс")}</span>
                    <span>1 FTF = {rate || 0} USDT</span>
                </div>
                <div className={`${Form.infoRow} ${Form.infoRowTotal}`}>
                    <span>{t("Общая цена")}</span>
                    <span>{amountNum > 0 ? `${fmt2(total)} USDT` : '—'}</span>
                </div>
            </div>

            <button
                type="button"
                className={`${Form.btnAccent} ${canConfirm ? '' : Form.btnDisabled}`}
                onClick={handleConfirm}
            >
                {t("Подтвердить")}
            </button>
        </DsModal>
    );
};

export default CabinetMatrixBuyModal;
